import { useState, useEffect, useRef } from "react";
import axios from "axios";
import usePhaseRedirect from "./usePhaseRedirect";

export default function IftarDashboard() {
  usePhaseRedirect(); // redirects on mount based on current phase
  const [questions, setQuestions] = useState([]);
  const [status, setStatus] = useState("idle");
  const [speakingRow, setSpeakingRow] = useState(null);
  const [activeIndex, setActiveIndex] = useState(null);

  const statusPollRef = useRef(null);
  const speakPollRef = useRef(null);
  const revealPollRef = useRef(null);
  const hasData = useRef(false);
  const currentAudioRef = useRef(null);
  const lastSpeakingRowRef = useRef(null);
  const audioContextRef = useRef(null);

  // ── Lazily get-or-create AudioContext, always resumed ──
  const getAudioContext = async () => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    const ctx = audioContextRef.current;
    // FIX BUG 3: Always resume before use — don't rely on user gesture listeners
    if (ctx.state === "suspended") {
      await ctx.resume();
    }
    return ctx;
  };

  // Unlock AudioContext on any user interaction (belt-and-suspenders)
  useEffect(() => {
    const unlock = async () => {
      if (audioContextRef.current && audioContextRef.current.state === "suspended") {
        await audioContextRef.current.resume().catch(() => {});
      }
    };
    window.addEventListener("click", unlock);
    window.addEventListener("keydown", unlock);
    window.addEventListener("touchstart", unlock);
    return () => {
      window.removeEventListener("click", unlock);
      window.removeEventListener("keydown", unlock);
      window.removeEventListener("touchstart", unlock);
    };
  }, []);

  // Cleanup AudioContext on unmount
  useEffect(() => {
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close().catch(() => {});
      }
    };
  }, []);

  // Poll status every 1 second
  useEffect(() => {
    const pollStatus = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_PATH_DESTINATION}/iftarExchangeGetStatus`
        );
        const data = res.data;
        const currentStatus = Array.isArray(data) ? data[0]?.status : data?.status;

        if (currentStatus === 0) {
          hasData.current = false;
          setQuestions([]);
          setStatus("idle");
          setSpeakingRow(null);
          setActiveIndex(null);
        } else if (currentStatus === 2) {
          if (!hasData.current) setStatus("processing");
        } else if (currentStatus === 1) {
          if (!hasData.current) fetchSummarize();
        }
      } catch (err) {
        console.error("Status polling error:", err);
      }
    };

    statusPollRef.current = setInterval(pollStatus, 1000);
    pollStatus();
    return () => clearInterval(statusPollRef.current);
  }, []);

  // Poll speaking row every 600ms
  useEffect(() => {
    if (status !== "ready" && status !== "revealing") return;

    const pollSpeakRow = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_PATH_DESTINATION}/iftarExchangeGetSpeakRow`
        );
        const data = res.data;
        const rowIndex = Array.isArray(data) ? data[0]?.rowIndex : data?.rowIndex;
        setSpeakingRow(rowIndex !== undefined && rowIndex !== null ? Number(rowIndex) : null);
      } catch (err) {
        console.warn("Speak row polling error:", err);
      }
    };

    speakPollRef.current = setInterval(pollSpeakRow, 600);
    pollSpeakRow();
    return () => clearInterval(speakPollRef.current);
  }, [status]);

  // Poll reveal row every 600ms
  useEffect(() => {
    if (status !== "ready" && status !== "revealing") return;

    const pollRevealRow = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_PATH_DESTINATION}/iftarExchangeGetRevealRow`
        );
        const data = res.data;
        const count = Array.isArray(data) ? data[0]?.revealCount : data?.revealCount;
        const val = count !== undefined && count !== null ? Number(count) : 0;
        setActiveIndex(val === 0 ? null : val - 1);
        if (val > 0) setStatus("revealing");
      } catch (err) {
        console.warn("Reveal row polling error:", err);
      }
    };

    revealPollRef.current = setInterval(pollRevealRow, 600);
    pollRevealRow();
    return () => clearInterval(revealPollRef.current);
  }, [status]);

  // 🔊 Play TTS when speakingRow changes
  useEffect(() => {
    const isNewRow = speakingRow !== null && speakingRow !== lastSpeakingRowRef.current;

    if (!isNewRow) {
      if (speakingRow === null && currentAudioRef.current) {
        try { currentAudioRef.current.stop(); } catch {}
        currentAudioRef.current = null;
      }
      return;
    }

    lastSpeakingRowRef.current = speakingRow;
    const text = questions[speakingRow]?.en;
    if (!text) return;

    if (currentAudioRef.current) {
      try { currentAudioRef.current.stop(); } catch {}
      currentAudioRef.current = null;
    }

    const playTTS = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_PATH_DESTINATION}/iftarExchangeTTS`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ text }),
          }
        );

        if (!response.ok) throw new Error("TTS request failed");

        const arrayBuffer = await response.arrayBuffer();

        // FIX BUG 3: Always get a resumed context right before decoding/playing
        const ctx = await getAudioContext();

        const audioBuffer = await ctx.decodeAudioData(arrayBuffer);
        const source = ctx.createBufferSource();
        source.buffer = audioBuffer;
        source.connect(ctx.destination);
        currentAudioRef.current = source;

        source.onended = () => {
          currentAudioRef.current = null;
          lastSpeakingRowRef.current = null;
          fetch(`${process.env.REACT_APP_PATH_DESTINATION}/iftarExchangeSetSpeakRow`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ rowIndex: null }),
          }).catch(() => {});
        };

        source.start(0);
      } catch (err) {
        console.error("TTS error:", err);
        currentAudioRef.current = null;
        lastSpeakingRowRef.current = null;
      }
    };

    playTTS();
  }, [speakingRow, questions]);

  const fetchSummarize = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_PATH_DESTINATION}/iftarExchangeGetSummarize`
      );
      const data = res.data;

      if (Array.isArray(data) && data.length > 0) {
        hasData.current = true;
        clearInterval(statusPollRef.current);

        const formatted = data.map((row, i) => ({
          id: String(i + 1).padStart(2, "0"),
          en: row.question,
        }));
        setQuestions(formatted);
        setStatus("ready");
      }
    } catch (err) {
      console.error("Fetch summarize error:", err);
    }
  };

  return (
    <div style={{
      height: "100vh",
      background: "linear-gradient(180deg, #07111f 0%, #0a1628 60%, #061018 100%)",
      fontFamily: "'Segoe UI', system-ui, sans-serif",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      boxSizing: "border-box",
    }}>

      {/* Top Bar */}
      <div style={{
        flexShrink: 0,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "10px 28px",
        borderBottom: "1px solid rgba(255,255,255,0.07)",
        background: "rgba(0,0,0,0.25)",
      }}>
        <span style={{
          fontSize: "clamp(10px, 1.2vw, 13px)",
          fontWeight: "600",
          letterSpacing: "3px",
          color: "rgba(255,255,255,0.4)",
          textTransform: "uppercase",
          display: "flex",
          alignItems: "center",
          gap: "6px",
        }}>
          <span style={{ color: "#e8c84a", fontSize: "15px" }}>●</span>
          ROUNDTABLE DISCUSSION
        </span>
        <span style={{
          fontSize: "clamp(10px, 1.2vw, 13px)",
          letterSpacing: "2px",
          color: "rgba(255,255,255,0.3)",
          textTransform: "uppercase",
        }}>Iftar Exchange 2026</span>
      </div>

      {/* Main Content */}
      <div style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        position: "relative",
      }}>

        {/* ── BANNER (idle) ── */}
        {status === "idle" && (
          <div style={{
            position: "fixed", inset: 0, zIndex: 10,
            backgroundColor: "#07111f",
            display: "flex", alignItems: "center", justifyContent: "center",
            animation: "fadeIn 0.6s ease",
          }}>
            <img
              src="/dist/img/IftarExchangeBanner.png"
              alt="Iftar Exchange 2026"
              style={{ width: "100%", height: "100%", objectFit: "contain" }}
            />
          </div>
        )}

        {/* ── PROCESSING ── */}
        {status === "processing" && (
          <div style={{
            position: "fixed", inset: 0, zIndex: 10,
            backgroundColor: "#07111f",
            display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center",
            gap: "28px", animation: "fadeIn 0.5s ease",
          }}>
            <img
              src="/dist/img/IftarExchangeBanner.png"
              alt="Iftar Exchange 2026"
              style={{
                position: "absolute", inset: 0,
                width: "100%", height: "100%",
                objectFit: "contain", opacity: 0.15,
              }}
            />
            <div style={{ position: "relative", zIndex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: "24px" }}>
              <div style={{ position: "relative", width: "80px", height: "80px" }}>
                <div style={{ position: "absolute", inset: 0, borderRadius: "50%", border: "3px solid rgba(232,200,74,0.15)" }} />
                <div style={{
                  position: "absolute", inset: 0, borderRadius: "50%",
                  border: "3px solid transparent",
                  borderTopColor: "#e8c84a", borderRightColor: "rgba(232,200,74,0.4)",
                  animation: "spinCircle 1s linear infinite",
                }} />
                <div style={{
                  position: "absolute", inset: "50%",
                  transform: "translate(-50%, -50%)",
                  width: "8px", height: "8px", borderRadius: "50%",
                  background: "#e8c84a", boxShadow: "0 0 10px rgba(232,200,74,0.8)",
                }} />
              </div>
              <div style={{ textAlign: "center" }}>
                <p style={{ margin: "0 0 6px", fontSize: "13px", fontWeight: "700", letterSpacing: "3px", color: "#e8c84a", textTransform: "uppercase" }}>
                  Processing with AI
                </p>
                <p style={{ margin: 0, fontSize: "11px", letterSpacing: "2px", color: "rgba(255,255,255,0.35)", textTransform: "uppercase" }}>
                  Generating discussion questions...
                </p>
              </div>
              <div style={{ display: "flex", gap: "6px" }}>
                {[0, 1, 2].map(i => (
                  <div key={i} style={{
                    width: "5px", height: "5px", borderRadius: "50%",
                    background: "rgba(232,200,74,0.6)",
                    animation: `dotPulse 1.4s ease-in-out ${i * 0.2}s infinite`,
                  }} />
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── READY (waiting for presenter) ── */}
        {status === "ready" && (
          <div style={{
            position: "fixed", inset: 0, zIndex: 10,
            backgroundColor: "#07111f",
            display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center",
            gap: "32px", animation: "fadeIn 0.7s ease",
          }}>
            <img
              src="/dist/img/IftarExchangeBanner.png"
              alt="Iftar Exchange 2026"
              style={{
                position: "absolute", inset: 0,
                width: "100%", height: "100%",
                objectFit: "contain", opacity: 0.1,
              }}
            />
            <div style={{ position: "relative", zIndex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: "20px" }}>
              <div style={{
                width: "80px", height: "80px", borderRadius: "50%",
                background: "rgba(110, 231, 183, 0.1)",
                border: "2px solid rgba(110, 231, 183, 0.4)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "36px",
                animation: "popIn 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards",
              }}>✓</div>
              <div style={{ textAlign: "center" }}>
                <p style={{ margin: "0 0 8px", fontSize: "clamp(22px, 3vw, 32px)", fontWeight: "700", color: "#6ee7b7" }}>
                  Questions Ready!
                </p>
                <p style={{ margin: "0 0 4px", fontSize: "clamp(13px, 1.4vw, 16px)", color: "rgba(255,255,255,0.5)", letterSpacing: "1px" }}>
                  {questions.length} discussion questions generated
                </p>
                <p style={{ margin: 0, fontSize: "12px", color: "rgba(255,255,255,0.25)", letterSpacing: "2px", textTransform: "uppercase" }}>
                  Waiting for presenter to begin...
                </p>
              </div>
              <div style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
                {[0, 1, 2].map(i => (
                  <div key={i} style={{
                    width: "6px", height: "6px", borderRadius: "50%",
                    background: "rgba(110, 231, 183, 0.5)",
                    animation: `dotPulse 1.8s ease-in-out ${i * 0.3}s infinite`,
                  }} />
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── SINGLE QUESTION (revealing) ── */}
        {status === "revealing" && activeIndex !== null && (() => {
          const q = questions[activeIndex];
          if (!q) return null;
          const isSpeaking = speakingRow === activeIndex;

          return (
            <div
              key={activeIndex}
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "clamp(24px, 5vh, 60px) clamp(32px, 8vw, 100px)",
              }}
            >
              <div style={{
                width: "100%",
                maxWidth: "960px",
                display: "flex",
                flexDirection: "column",
                gap: "clamp(18px, 3vh, 32px)",
                animation: "questionEntrance 0.55s cubic-bezier(0.2, 0, 0, 1) forwards",
              }}>

                {/* Number + label row */}
                <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                  <span style={{
                    fontSize: "clamp(52px, 8vw, 96px)",
                    fontWeight: "800",
                    color: isSpeaking ? "rgba(232,200,74,0.55)" : "rgba(255,255,255,0.1)",
                    letterSpacing: "-2px",
                    fontVariantNumeric: "tabular-nums",
                    lineHeight: 1,
                    transition: "color 0.3s ease",
                  }}>{q.id}</span>

                  <div style={{
                    height: "clamp(44px, 6vw, 76px)",
                    width: "4px",
                    borderRadius: "2px",
                    background: isSpeaking
                      ? "linear-gradient(180deg, #f7d75a 0%, #e8a000 100%)"
                      : "linear-gradient(180deg, #f7d75a 0%, #3e94d2 100%)",
                    boxShadow: isSpeaking ? "0 0 14px rgba(232,200,74,0.6)" : "none",
                    transition: "all 0.3s ease",
                    flexShrink: 0,
                  }} />

                  <span style={{
                    fontSize: "clamp(10px, 1.1vw, 13px)",
                    letterSpacing: "3px",
                    color: "rgba(255,255,255,0.28)",
                    textTransform: "uppercase",
                    fontWeight: "600",
                  }}>DISCUSSION QUESTION</span>
                </div>

                {/* Question text */}
                <p style={{
                  margin: 0,
                  fontSize: "clamp(22px, 3.4vw, 46px)",
                  fontWeight: "700",
                  color: isSpeaking ? "#ffffff" : "rgba(255,255,255,0.92)",
                  lineHeight: "1.35",
                  transition: "color 0.3s ease",
                }}>{q.en}</p>

                {/* Speaking wave */}
                {isSpeaking && (
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "5px",
                    animation: "fadeIn 0.3s ease",
                  }}>
                    {[0, 1, 2, 3, 4, 5, 6].map(j => (
                      <div key={j} style={{
                        width: "4px",
                        background: "#e8c84a",
                        borderRadius: "2px",
                        animation: `waveBar 0.7s ease-in-out ${j * 0.07}s infinite alternate`,
                        opacity: 0.85,
                      }} />
                    ))}
                    <span style={{
                      marginLeft: "12px",
                      fontSize: "22px",
                      animation: "pulse 1.2s ease-in-out infinite",
                    }}>🔊</span>
                  </div>
                )}

                {/* Footer label */}
                <div style={{
                  marginTop: "clamp(6px, 1vh, 12px)",
                  fontSize: "11px",
                  letterSpacing: "3px",
                  color: "rgba(255,255,255,0.18)",
                  textTransform: "uppercase",
                }}>
                  Iftar Exchange 2026 · Question {q.id} of {questions.length}
                </div>
              </div>
            </div>
          );
        })()}
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes popIn {
          from { opacity: 0; transform: scale(0.5); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes questionEntrance {
          from { opacity: 0; transform: translateY(40px) scale(0.97); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes spinCircle {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes dotPulse {
          0%, 80%, 100% { opacity: 0.2; transform: scale(0.8); }
          40% { opacity: 1; transform: scale(1.2); }
        }
        @keyframes waveBar {
          from { height: 6px; }
          to { height: 30px; }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
    </div>
  );
}